import React, { useState } from 'react';
import { X, Calendar, MapPin, Users, DollarSign } from 'lucide-react';

const BusRentalModal = ({ apiCall, groups, buses, onClose, onSuccess, showError, showSuccess }) => {
  const [formData, setFormData] = useState({
    groupId: '',
    tripDate: '',
    destination: '',
    departureTime: '',
    returnTime: '',
    numberOfBuses: 1,
    numberOfPassengers: '',
    basePrice: '',
    discountPercent: 0,
    notes: ''
  });

  const [loading, setLoading] = useState(false);

  const calculateFinalPrice = () => {
    const base = parseFloat(formData.basePrice) || 0;
    const discount = parseFloat(formData.discountPercent) || 0;
    return base - (base * discount / 100);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.groupId || !formData.tripDate || !formData.destination || !formData.basePrice) {
      showError('Please fill in all required fields');
      return;
    }

    try {
      setLoading(true);
      await apiCall('/bus/rental-trips', {
        method: 'POST',
        body: JSON.stringify({
          groupId: parseInt(formData.groupId),
          tripDate: formData.tripDate,
          destination: formData.destination,
          departureTime: formData.departureTime || null,
          returnTime: formData.returnTime || null,
          numberOfBuses: parseInt(formData.numberOfBuses),
          numberOfPassengers: formData.numberOfPassengers ? parseInt(formData.numberOfPassengers) : null,
          basePrice: parseFloat(formData.basePrice),
          discountPercent: parseFloat(formData.discountPercent) || 0,
          notes: formData.notes || null
        })
      });
      onSuccess();
    } catch (error) {
      showError('Error creating trip: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const formatUSD = (amount) => {
    return '$' + new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(amount);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="p-6 border-b border-gray-200 flex justify-between items-center sticky top-0 bg-white">
          <h2 className="text-2xl font-bold text-gray-800">New Trip Rental</h2>
          <button 
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
          >
            <X size={24} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6">
          <div className="space-y-6">
            {/* Group Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Users className="w-4 h-4 inline mr-1" />
                Group *
              </label>
              <select
                value={formData.groupId}
                onChange={(e) => setFormData({ ...formData, groupId: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                required
              >
                <option value="">Select a group</option>
                {groups.map(group => (
                  <option key={group.GroupID} value={group.GroupID}>
                    {group.GroupName}
                  </option>
                ))}
              </select>
            </div>

            {/* Trip Date & Times */}
            <div className="grid md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Calendar className="w-4 h-4 inline mr-1" />
                  Trip Date *
                </label>
                <input
                  type="date"
                  value={formData.tripDate}
                  onChange={(e) => setFormData({ ...formData, tripDate: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Departure Time
                </label>
                <input
                  type="time"
                  value={formData.departureTime}
                  onChange={(e) => setFormData({ ...formData, departureTime: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Return Time
                </label>
                <input
                  type="time"
                  value={formData.returnTime}
                  onChange={(e) => setFormData({ ...formData, returnTime: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            {/* Destination */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <MapPin className="w-4 h-4 inline mr-1" />
                Destination *
              </label>
              <input
                type="text"
                value={formData.destination}
                onChange={(e) => setFormData({ ...formData, destination: e.target.value })}
                placeholder="e.g., Mountain Camp, Byblos, etc."
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>

            {/* Buses & Passengers */}
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Number of Buses *
                </label>
                <input
                  type="number"
                  min="1"
                  value={formData.numberOfBuses}
                  onChange={(e) => setFormData({ ...formData, numberOfBuses: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  required
                />
                <p className="text-xs text-gray-500 mt-1">Available: {buses.length} buses</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Number of Passengers
                </label>
                <input
                  type="number"
                  min="0"
                  value={formData.numberOfPassengers}
                  onChange={(e) => setFormData({ ...formData, numberOfPassengers: e.target.value })}
                  placeholder="Optional"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            {/* Pricing */}
            <div className="bg-blue-50 rounded-lg p-4 space-y-4">
              <h3 className="font-semibold text-gray-800 flex items-center gap-2">
                <DollarSign className="w-4 h-4" />
                Pricing
              </h3>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Base Price (USD) *
                  </label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={formData.basePrice}
                    onChange={(e) => setFormData({ ...formData, basePrice: e.target.value })}
                    placeholder="e.g., 500"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Discount (%)
                  </label>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    step="0.1"
                    value={formData.discountPercent}
                    onChange={(e) => setFormData({ ...formData, discountPercent: e.target.value })}
                    placeholder="0"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
              
              {formData.basePrice && (
                <div className="pt-4 border-t border-blue-200">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-gray-600">Base Price:</span>
                    <span className="font-semibold">{formatUSD(parseFloat(formData.basePrice) || 0)}</span>
                  </div>
                  {formData.discountPercent > 0 && (
                    <div className="flex justify-between items-center mb-2 text-green-600">
                      <span>Discount ({formData.discountPercent}%):</span>
                      <span>-{formatUSD((parseFloat(formData.basePrice) || 0) * (parseFloat(formData.discountPercent) || 0) / 100)}</span>
                    </div>
                  )}
                  <div className="flex justify-between items-center text-lg font-bold text-blue-600 pt-2 border-t border-blue-200">
                    <span>Final Price:</span>
                    <span>{formatUSD(calculateFinalPrice())}</span>
                  </div>
                </div>
              )}
            </div>

            {/* Notes */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Notes
              </label>
              <textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                rows="3"
                placeholder="Any special requirements or notes..."
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          {/* Footer */}
          <div className="flex gap-3 mt-6 pt-6 border-t">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-6 py-3 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              {loading ? 'Creating...' : 'Create Trip Rental'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default BusRentalModal;